import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AuthLoadingScreen
 * -----------------
 * Branded full-page loader shown while the auth bootstrap is in progress
 * (MSAL handshake → JWT exchange → session restore).
 *
 * Goals:
 *   - Replace the bare <CircularProgress /> "blank page" experience.
 *   - Communicate *what* is happening so the wait feels intentional.
 *   - Use a small minimum-display delay so very fast loads don't flash
 *     the spinner, but slower loads (cold backend, JIT user creation,
 *     network hop) get a polished progress affordance.
 *
 * Pure presentational component — no auth side effects.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { icons } from '../../assets/icons';
import { AppColors, AppFont } from '../../styles/colors';
const PHASE_COPY = {
    'awaiting-msal': {
        title: 'Connecting to single sign-on',
        subtitle: 'Verifying your Microsoft account…',
    },
    restoring: {
        title: 'Restoring your session',
        subtitle: 'Just a moment while we pick up where you left off…',
    },
    exchanging: {
        title: 'Signing you in',
        subtitle: 'Securely preparing your workspace…',
    },
};
const FALLBACK_COPY = {
    title: 'Loading',
    subtitle: 'Please wait…',
};
export const AuthLoadingScreen = ({ phase, message, minDisplayMs = 250, }) => {
    // Hold a "visible" flag so React can't unmount us before minDisplayMs
    // elapses. The parent drives the actual lifetime; this just guarantees
    // the fade-in has time to complete on snappy logins.
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const t = window.setTimeout(() => setShown(true), 0);
        const min = window.setTimeout(() => setShown(true), minDisplayMs);
        return () => {
            window.clearTimeout(t);
            window.clearTimeout(min);
        };
    }, [minDisplayMs]);
    const copy = phase && phase !== 'ready' && phase !== 'idle'
        ? PHASE_COPY[phase]
        : FALLBACK_COPY;
    const title = message !== null && message !== void 0 ? message : copy.title;
    return (_jsx(Fade, { in: shown, timeout: 250, appear: true, children: _jsxs(Box, { role: "status", "aria-live": "polite", "aria-busy": "true", sx: {
                position: 'fixed',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                backgroundColor: AppColors.appBackground,
                fontFamily: AppFont.family,
                px: 3,
                textAlign: 'center',
                zIndex: (theme) => theme.zIndex.modal + 1,
            }, children: [_jsx(Box, { component: "img", src: icons.deloitteLogo, alt: "Deloitte", sx: {
                        height: 48,
                        width: 'auto',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    } }), _jsx(CircularProgress, { size: 36, thickness: 4, sx: { color: AppColors.headerBackground } }), _jsxs(Box, { sx: { maxWidth: 420 }, children: [_jsx(Typography, { variant: "subtitle1", sx: {
                                color: AppColors.fontPrimary,
                                fontWeight: 600,
                                letterSpacing: 0.2,
                            }, children: title }), _jsx(Typography, { variant: "body2", sx: {
                                color: AppColors.fontDarkGray,
                                mt: 0.5,
                            }, children: copy.subtitle })] })] }) }));
};
export default AuthLoadingScreen;
